import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import {
  SettingsOutlined,
  BellCancelledOutlined,
  BellRingOutlined,
  ArchiveOutlined,
  TeacherAddOutlined,
} from "@toddle-edu/ds-icons";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import classNames from "classnames";
import { getUserType } from "modules/CommonSelectors";

import ACLStore from "lib/aclStore";
import ListPopup from "AppComponents/ListPopup";
import { LIST_POPUP_DIRECTION } from "Constants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import {
  getFormattedNameForUserType,
  CHAT_TYPE_ENUM,
  getProfileItem,
  recordChatActions,
  MESSAGING_ACTIONS_FROM,
  MESSAGING_ACTIONS,
  recordChannelActions,
  isPaidConnectModule,
  isChatToChannelEnabled,
  getSubText as getThreadSubtext,
  isMessagingRevampV2Enabled,
  isChannelSplitFeatureEnabled,
  isMessagingDisabledForFamily,
  isMessagingDisabledForStudent,
} from "MessagingV2/modules/Utils";

import { ADMIN, PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import ConditionalWrapper from "Utils/ConditionalWrapper";

import {
  toggleChatMute,
  unarchiveOrJoinChannel,
} from "Platform/routes/MessagingV2/modules/MessagingModule";
import classes from "./HeaderUtils.scss";

export const EDIT_INFO = "EDIT_INFO";
export const JOIN_CHANNEL = "JOIN_CHANNEL";
export const MANAGE_MEMBERS = "MANAGE_MEMBERS";
export const TOGGLE_MUTE = "TOGGLE_MUTE";
export const CHANNEL_SETTINGS = "CHANNEL_SETTINGS";
export const CHANNEL_OVERVIEW_AND_SETTINGS = "CHANNEL_OVERVIEW_AND_SETTINGS";
export const ARCHIVE = "ARCHIVE";
export const UNARCHIVE = "UNARCHIVE";
export const ADD_MEMBERS = "ADD_MEMBERS";

const styles = {
  tooltipOverlayStyle: {
    maxWidth: "500px",
  },
};

const DynamicAdjustableText = ({ headerTitle }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const scrollWidth = titleRef?.current?.scrollWidth;
      const clientWidth = titleRef?.current?.clientWidth;
      const newShowTooltip = scrollWidth > clientWidth;
      setShowTooltip(newShowTooltip);
    };
    const currentObserver = new ResizeObserver(handleResize);
    const currentContainerRef = titleRef.current;
    if (currentContainerRef) {
      currentObserver.observe(currentContainerRef);
    }
    return () =>
      currentContainerRef && currentObserver.unobserve(currentContainerRef);
  }, []);

  return (
    <ConditionalWrapper
      wrapper={
        <Tooltip
          tooltip={headerTitle}
          overlayInnerStyle={styles.tooltipOverlayStyle}
        />
      }
      condition={showTooltip}
      type={"wrapperAsElement"}
    >
      <div ref={titleRef} className={classes.truncatedText}>
        {headerTitle}
      </div>
    </ConditionalWrapper>
  );
};

const filterChannelOptions = ({ dropdownOptions, conversationType }) => {
  return _.filter(dropdownOptions, option => {
    const perm = _.get(option, "perm", "");
    const visibleChatTypes = _.get(option, "visibleChatTypes", []);
    const showDropdownOption = _.get(option, "showDropdownOption", true);
    const isDivider = _.get(option, "isDivider", false);

    if (isDivider) {
      return (
        showDropdownOption && _.includes(visibleChatTypes, conversationType)
      );
    }
    return (
      showDropdownOption &&
      _.includes(visibleChatTypes, conversationType) &&
      (!perm || ACLStore.can(perm))
    );
  });
};

export const channelActionOptions = ({
  t,
  isArchived,
  isMuted,
  isCurrentUserNotInChannel,
  conversationType,
  canManageChannel,
  toggleChatMuteHandler,
}) => {
  const isPublicChannelEnable = isChannelSplitFeatureEnabled();
  if (isPublicChannelEnable && !isCurrentUserNotInChannel && !isArchived) {
    const tooltip = t(
      isMuted ? "common:unmute_with_label" : "common:mute_with_label",
      {
        label: t("common:channel"),
      }
    );
    return (
      <Tooltip tooltip={tooltip}>
        <IconButton
          dsVersion="2.0"
          variant="neutral"
          type="outlined"
          size="medium"
          onClick={toggleChatMuteHandler}
          className={classes.channelIconButton}
          icon={isMuted ? <BellCancelledOutlined /> : <BellRingOutlined />}
        />
      </Tooltip>
    );
  }
  if (isArchived) {
    //todo add perm check to archive channel
    const unArchiveDropdownOption = [
      {
        icon: <ArchiveOutlined />,
        key: UNARCHIVE,
        label: t("common:unarchive_with_label", {
          label: t("common:channel"),
        }),
        visibleChatTypes: [
          CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
          CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
        ],
        showDropdownOption: canManageChannel,
      },
    ];
    return filterChannelOptions({
      dropdownOptions: unArchiveDropdownOption,
      conversationType,
    });
  }

  const joinChannelDropdownOption = {
    key: JOIN_CHANNEL,
    icon: <TeacherAddOutlined />,
    label: t("common:join"),
    visibleChatTypes: [
      CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
      CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
    ],
  };

  const unmuteDropdownOption = {
    icon: <BellCancelledOutlined />,
    label: t("common:unmute_with_label", {
      label: t("common:channel"),
    }),
  };

  const archiveDropdownOption = {
    key: ARCHIVE,
    icon: <ArchiveOutlined />,
    isDestructive: true,
    label: t("common:archive_label", {
      label: t("common:channel"),
    }),
    visibleChatTypes: [
      CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
      CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
    ],
    showDropdownOption: canManageChannel,
  };

  if (
    isCurrentUserNotInChannel &&
    !_.includes(
      [CHAT_TYPE_ENUM.CHANNEL_CUSTOM, CHAT_TYPE_ENUM.CHANNEL_PUBLIC],
      conversationType
    )
  ) {
    return [joinChannelDropdownOption, archiveDropdownOption];
  }
  /*   const isUIRevempEnabled = isMessagingRevempEnabled();
  const isChatToChannelOptionEnabled = isChatToChannelEnabled();
 */
  const dropdownOptions = [
    {
      key: CHANNEL_OVERVIEW_AND_SETTINGS,
      icon: <SettingsOutlined />,
      label: t("common:overview_and_settings"),
      showDropdownOption: canManageChannel,
      visibleChatTypes: [
        CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
        CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
        CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED,
        CHAT_TYPE_ENUM.CHANNEL_FAMILY,
      ],
    },
    /*   {
      key: EDIT_INFO,
      icon: <PencilLineOutlined />,
      label: t("common:edit_channel_information"),
      visibleChatTypes: [CHAT_TYPE_ENUM.CHANNEL_CUSTOM],
      showDropdownOption: canManageChannel && !isUIRevempEnabled,
    },
    {
      key: MANAGE_MEMBERS,
      icon: <ManageMembersOutlined />,
      label: t("common:manage_with_label", { label: t("common:members") }),
      visibleChatTypes: [CHAT_TYPE_ENUM.CHANNEL_CUSTOM],
      showDropdownOption: canManageChannel && !isUIRevempEnabled,
    }, */
    {
      key: TOGGLE_MUTE,
      icon: <BellRingOutlined />,
      label: t("common:mute_with_label", {
        label: t("common:channel"),
      }),
      visibleChatTypes: [
        CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
        CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
        CHAT_TYPE_ENUM.CHANNEL_FAMILY,
        CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED,
      ],
      ...(isMuted && unmuteDropdownOption),
    },
    /* {
      key: ADD_MEMBERS,
      icon: <TeacherAddOutlined />,
      label: t("common:add_members"),
      visibleChatTypes: [CHAT_TYPE_ENUM.CHANNEL_FAMILY],
      showDropdownOption: isChatToChannelOptionEnabled && !isUIRevempEnabled,
    },
    {
      key: CHANNEL_SETTINGS,
      icon: <SettingsOutlined />,
      label: t("common:channel_settings"),
      showDropdownOption: canManageChannel && !isUIRevempEnabled,
      visibleChatTypes: [
        CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
        CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED,
      ],
    }, */
    {
      key: "divider",
      isDivider: true,
      showDropdownOption: canManageChannel,
      visibleChatTypes: [
        CHAT_TYPE_ENUM.CHANNEL_CUSTOM,
        CHAT_TYPE_ENUM.CHANNEL_PUBLIC,
      ],
    },
    archiveDropdownOption,
  ];

  return filterChannelOptions({ dropdownOptions, conversationType });
};

export const chatActionOptions = ({ t, isArchived, isMuted, onClick }) => {
  if (isArchived) {
    return null;
  }

  if (!isChatToChannelEnabled()) {
    const tooltip = t(
      isMuted ? "common:unmute_with_label" : "common:mute_with_label",
      {
        label: t("common:chat"),
      }
    );

    return (
      <Tooltip tooltip={tooltip}>
        <IconButton
          size={"small"}
          variant={"outlined-subtle"}
          onClick={onClick}
          className={classNames({
            [classes.iconButton]: true,
          })}
          icon={isMuted ? <BellCancelledOutlined /> : <BellRingOutlined />}
        />
      </Tooltip>
    );
  }

  const muteUnmuteLabel = t(
    isMuted ? "common:unmute_with_label" : "common:mute_with_label",
    {
      label: t("common:chat"),
    }
  );

  const dropdownOptions = [
    {
      key: TOGGLE_MUTE,
      icon: isMuted ? <BellCancelledOutlined /> : <BellRingOutlined />,
      label: muteUnmuteLabel,
    },
  ];

  const showAddMemberOption = getUserType() === STAFF && isPaidConnectModule();

  if (showAddMemberOption) {
    dropdownOptions.push({
      key: ADD_MEMBERS,
      icon: <TeacherAddOutlined />,
      label: t("common:add_members"),
    });
  }

  return dropdownOptions;
};

//  action handler

export const channelActionHandler = ({
  key,
  chatId,
  isMuted,
  conversationType,
  isMemberUpdate,
  setChannelOptionsModalState,
}) => {
  return async dispatch => {
    switch (key) {
      case EDIT_INFO:
        {
          setChannelOptionsModalState(EDIT_INFO);
          recordChannelActions({
            type: MESSAGING_ACTIONS.BASIC_INFORMATION,
          });
        }
        break;
      case CHANNEL_OVERVIEW_AND_SETTINGS:
        setChannelOptionsModalState(CHANNEL_OVERVIEW_AND_SETTINGS);
        break;
      case MANAGE_MEMBERS:
        setChannelOptionsModalState(MANAGE_MEMBERS);
        recordChannelActions({
          type: MESSAGING_ACTIONS.MEMBERS,
          mode: isMemberUpdate ? "EDIT" : "VIEW",
        });
        break;
      case UNARCHIVE:
        setChannelOptionsModalState(UNARCHIVE);
        break;
      case ARCHIVE:
        setChannelOptionsModalState(ARCHIVE);
        break;
      case TOGGLE_MUTE:
        {
          dispatch(toggleChatMute({ id: chatId, isMuted, isChannel: true }));
          recordChatActions({
            action: !isMuted
              ? MESSAGING_ACTIONS.MUTE
              : MESSAGING_ACTIONS.UNMUTE,
            chatId,
            from: MESSAGING_ACTIONS_FROM.CHAT_ACTIONS,
            chatType: conversationType,
          });
        }

        break;
      case CHANNEL_SETTINGS:
        {
          setChannelOptionsModalState(CHANNEL_SETTINGS);
          recordChannelActions({
            type: MESSAGING_ACTIONS.CHANNEL_SETTING,
          });
        }
        break;
      case JOIN_CHANNEL:
        {
          recordChatActions({
            action: MESSAGING_ACTIONS.JOIN,
            chatId,
            from: MESSAGING_ACTIONS_FROM.CHAT_ACTIONS,
            chatType: conversationType,
          });
          dispatch(unarchiveOrJoinChannel({ chatId, joinChannel: true }));
        }
        break;
      case ADD_MEMBERS: {
        setChannelOptionsModalState(ADD_MEMBERS);
      }
    }
  };
};

export const chatActionHandler = ({
  key,
  chatId,
  isMuted,
  conversationType,
  toggleChatMute,
  setOptionsModalState,
}) => {
  switch (key) {
    case TOGGLE_MUTE:
      toggleChatMute({ id: chatId, isMuted });
      recordChatActions({
        action: !isMuted ? MESSAGING_ACTIONS.MUTE : MESSAGING_ACTIONS.UNMUTE,
        chatId,
        from: MESSAGING_ACTIONS_FROM.CHAT_ACTIONS,
        chatType: conversationType,
      });
      break;
    case ADD_MEMBERS:
      setOptionsModalState(ADD_MEMBERS);
      break;
  }
};

const getStaffHeaderSubtext = ({
  messagedUserObj,
  courseFilter = [],
  t,
  isWidget,
}) => {
  const courses = _.get(messagedUserObj, "courses", []);
  const courseFilterSet = new Set(courseFilter);
  const filteredCourses = _.isEmpty(courseFilter)
    ? courses
    : _.filter(courses, course => courseFilterSet.has(course.id));
  const staffRole = _.get(messagedUserObj, "role");
  const isAdmin = _.isEqual(staffRole, ADMIN);

  return (
    <div className={classes.subtextContainer}>
      {messagedUserObj.displayRole}
      {!isAdmin ? (
        <>
          <div className={classes.divider} />
          <ListPopup
            direction={
              isWidget ? LIST_POPUP_DIRECTION.LEFT : LIST_POPUP_DIRECTION.RIGHT
            }
            label={t("common:class_plural")}
            singleLabel={t("common:class")}
            keyToMap="title"
            data={filteredCourses}
            id="courses"
            overrideClasses={{
              buttonLabel: "text-body-2",
            }}
          />
        </>
      ) : null}
    </div>
  );
};

const getStudentHeaderSubtext = ({
  messagedUserObj,
  courseFilter = [],
  t,
  isWidget,
}) => {
  const courses = _.get(messagedUserObj, "courses", []);
  const courseFilterSet = new Set(courseFilter);
  const filteredCourses = _.isEmpty(courseFilter)
    ? courses
    : _.filter(courses, course => courseFilterSet.has(course.id));

  const grade = _.get(messagedUserObj, "yearGroup.grade.name");

  const label = grade
    ? t("common:student_hyphen_label", {
        label: grade,
      })
    : t("common:student");

  return (
    <div className={classes.subtextContainer}>
      {label}
      <div className={classes.divider} />
      <ListPopup
        direction={
          isWidget ? LIST_POPUP_DIRECTION.LEFT : LIST_POPUP_DIRECTION.RIGHT
        }
        label={t("common:class_plural")}
        singleLabel={t("common:class")}
        keyToMap="title"
        data={filteredCourses}
        id="courses"
        overrideClasses={{
          buttonLabel: "text-body-2",
        }}
      />
    </div>
  );
};

/// header text

const getSubText = ({
  loggedInUserRole,
  messagedUserRole,
  messagedUserObj,
  t,
  defaultStudentNameSortPreference,
  isWidget,
  activeUserCourseIds,
}) => {
  if (loggedInUserRole === STAFF) {
    switch (messagedUserRole) {
      case STUDENT:
        return getStudentHeaderSubtext({
          messagedUserObj,
          t,
          isWidget,
          courseFilter: activeUserCourseIds,
        });

      case STAFF:
        return getStaffHeaderSubtext({
          messagedUserObj,
          t,
          isWidget,
          courseFilter: activeUserCourseIds,
        });

      case PARENT:
        {
          const childrenCount = _.size(messagedUserObj.children);
          if (childrenCount === 1) {
            return t("common:family_of_label", {
              label: getFormattedUserName({
                user: _.first(messagedUserObj.children),
                orderBy: defaultStudentNameSortPreference,
              }),
            });
          } else if (childrenCount > 1) {
            const studentNamesItems = _.map(
              messagedUserObj.children,
              student => ({
                title: getFormattedUserName({
                  user: student,
                  orderBy: defaultStudentNameSortPreference,
                }),
                id: student.id,
                profileImage: student.profileImage,
              })
            );

            return (
              <div className={classes.subtextContainer}>
                {t("common:family_of_label", {
                  label: "",
                })}
                <ListPopup
                  direction={
                    isWidget
                      ? LIST_POPUP_DIRECTION.LEFT
                      : LIST_POPUP_DIRECTION.RIGHT
                  }
                  label={t("common:student_lowercase_other")}
                  keyToMap="title"
                  data={studentNamesItems}
                  overrideClasses={{
                    buttonLabel: "text-body-2",
                  }}
                />
              </div>
            );
          }
        }
        return null;
      default:
        break;
    }
  } else {
    switch (messagedUserRole) {
      case STAFF:
        return getStaffHeaderSubtext({
          messagedUserObj,
          t,
          isWidget,
          courseFilter: activeUserCourseIds,
        });
      default:
        break;
    }
  }
};

export const getHeaderChatAvatarProps = ({
  user,
  defaultNameSortPreference,
  isArchived,
}) => {
  const { imageUrl: url, id, value: name } = getProfileItem({
    user,
    defaultNameSortPreference,
  });
  const userType = _.get(user, "type");

  let isDisabled = false;

  if (user) {
    const userType = _.get(user, "type");

    if (_.isEqual(userType, STUDENT)) {
      const gradeId = _.get(user, "yearGroup.grade.id");
      isDisabled = isMessagingDisabledForStudent(gradeId);
    } else if (_.isEqual(userType, PARENT)) {
      const children = _.get(user, "children", []);
      const gradeIds = _.map(children, child =>
        _.get(child, "yearGroup.grade.id")
      );
      isDisabled = isMessagingDisabledForFamily(gradeIds);
    }
  }

  return { url, id, name, isArchived, userType, isDisabled };
};

export const getHeaderChannelAvatarProps = ({
  channelLabel,
  profileImage,
  profileIcon,
  chatType,
  chatId,
}) => {
  return {
    url: profileImage,
    icon: profileIcon,
    id: chatId,
    name: channelLabel,
    chatType,
    hashtagIconStyle: { fontSize: "2rem" },
  };
};

export const getHeaderTextForChat = ({
  loggedInUserRole,
  user,
  t,
  defaultNameSortPreference,
  defaultStudentNameOrderPreference,
  isWidget,
  activeUserCourseIds,
  isUIRevampEnabledV2,
}) => {
  const { type } = user;
  const headerTitle = getFormattedNameForUserType({
    user,
    orderBy: defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
  });
  const subHeader = isUIRevampEnabledV2
    ? getThreadSubtext({
        loggedInUserRole,
        messagedUserRole: type,
        messagedUserObj: user,
        t,
        defaultStudentNameSortPreference: defaultNameSortPreference,
      })
    : getSubText({
        loggedInUserRole,
        messagedUserRole: type,
        messagedUserObj: user,
        t,
        defaultStudentNameSortPreference: defaultNameSortPreference,
        isWidget,
        activeUserCourseIds,
      });

  return {
    headerTitle: (
      <DynamicAdjustableText headerTitle={headerTitle} key={headerTitle} />
    ),
    subHeader,
  };
};

export const getHeaderTextForChannel = ({ label, description, isWidget }) => {
  const truncateLength = isWidget ? 30 : 80; // todo remove this const value and add truncate based on available space

  const isUIRevampEnabledV2 = isMessagingRevampV2Enabled();
  return {
    headerTitle: <DynamicAdjustableText headerTitle={label} key={label} />,
    subHeader: (
      <div>
        <ConditionalWrapper
          wrapper={
            <Tooltip
              tooltip={description}
              overlayInnerStyle={styles.tooltipOverlayStyle}
            />
          }
          condition={_.size(description) > truncateLength}
          type={"wrapperAsElement"}
        >
          <div
            className={classNames({
              ["text-body-s"]: isUIRevampEnabledV2,
            })}
            style={{ maxWidth: "500px" }}
          >
            {_.truncate(description, { length: truncateLength })}
          </div>
        </ConditionalWrapper>
      </div>
    ),
  };
};
